// user-dropdown v1: 用户下拉菜单组件

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { NotificationBadge } from '../../ui';
import { isUserLoggedIn } from '../utils/navbarHelpers';

import LogoutConfirmModal from './LogoutConfirmModal';
import UserAvatar from './UserAvatar';

/**
 * 用户下拉菜单组件
 * @param {Object} user - 用户对象
 * @param {Object} currentMockUser - 当前模拟用户
 * @param {boolean} showDropdown - 是否显示下拉菜单
 * @param {Function} onToggle - 切换下拉菜单事件
 * @param {Function} onSettingsClick - 设置按钮点击事件
 * @param {Function} onNotificationsClick - 通知按钮点击事件
 * @param {Function} onLogoutClick - 登出按钮点击事件
 * @param {number} unreadCount - 未读通知数量
 * @param {React.RefObject} dropdownRef - 下拉菜单引用
 * @param {string} className - 额外的CSS类名
 */
const UserDropdown = ({
  user,
  currentMockUser,
  showDropdown,
  onToggle,
  onHover,
  onLeave,
  onLogoutClick,
  unreadCount = 0,
  dropdownRef,
  className = '',
}) => {
  const isLoggedIn = isUserLoggedIn(user);
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [hideTimeout, setHideTimeout] = useState(null);
  const [isHovering, setIsHovering] = useState(false);

  // 处理头像点击 - 已登录跳艺术家主页，未登录跳登录页
  const handleAvatarClick = () => {
    if (isLoggedIn) {
      const guard = window.__TAG_NAV_GUARD__;
      const go = () => navigate('/artist/me');
      if (guard && typeof guard.request === 'function') guard.request(go);
      else go();
    } else {
      // 未登录：点击头像即代表"想登录"，直接跳登录页（2026-04-17 新增）
      navigate('/login');
    }
  };

  // 处理鼠标进入整个容器
  const handleContainerEnter = () => {
    if (isLoggedIn) {
      setIsHovering(true);
      if (hideTimeout) {
        clearTimeout(hideTimeout);
        setHideTimeout(null);
      }
      if (onHover) {
        onHover();
      }
    }
  };

  // 处理鼠标离开整个容器
  const handleContainerLeave = () => {
    if (isLoggedIn) {
      setIsHovering(false);
      const timeout = setTimeout(() => {
        if (!isHovering && onLeave) {
          onLeave();
        }
      }, 500); // 500ms 延迟
      setHideTimeout(timeout);
    }
  };

  // 处理登出按钮点击 - 显示确认弹窗
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // 处理确认登出
  const handleConfirmLogout = () => {
    onLogoutClick();
  };

  // 处理取消登出
  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  // 清理定时器
  useEffect(() => {
    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [hideTimeout]);

  return (
    <div
      className={`relative ${className}`}
      ref={dropdownRef}
      data-navbar-dropdown
      onMouseEnter={handleContainerEnter}
      onMouseLeave={handleContainerLeave}
    >
      {/* 用户头像 */}
      <div className='flex items-center relative'>
        <UserAvatar
          user={user}
          currentMockUser={currentMockUser}
          className='hover:opacity-80 transition-opacity duration-200 cursor-pointer'
          onClick={handleAvatarClick}
          unreadCount={unreadCount}
        />
      </div>

      {/* 连接桥梁 - 填补头像和下拉菜单之间的空隙 */}
      {isLoggedIn && showDropdown && (
        <div
          className='absolute right-0 top-full w-28 h-2 z-40'
          style={{ pointerEvents: 'auto' }}
        />
      )}

      {/* 下拉菜单 - 只在已登录时显示 */}
      {isLoggedIn && showDropdown && (
        <div className='absolute right-0 top-full mt-2 w-28 bg-white py-1 z-50 border border-gray-200'>
          {/* 设置选项 */}
          <Link
            to='/settings/edit-profile'
            className='block text-right px-2 py-3 text-sm font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors duration-200'
            onClick={() => {
              if (hideTimeout) {
                clearTimeout(hideTimeout);
              }
              onLeave();
            }}
          >
            SETTINGS
          </Link>

          {/* 通知选项 */}
          <Link
            to='/notifications'
            className='block text-right px-2 py-3 text-sm font-semibold text-gray-700 hover:bg-black hover:text-white transition-colors duration-200 relative'
            onClick={e => {
              e.preventDefault();
              if (hideTimeout) clearTimeout(hideTimeout);
              onLeave();
              const guard = window.__TAG_NAV_GUARD__;
              const go = () => navigate('/notifications');
              if (guard && typeof guard.request === 'function')
                guard.request(go);
              else go();
            }}
          >
            <div className='flex items-center justify-end gap-2'>
              {/* 未读通知数量标识 - 与头像上的同步，显示在左边 */}
              {unreadCount > 0 && (
                <div className='flex-shrink-0'>
                  <NotificationBadge unreadCount={unreadCount} size='sm' color='red' />
                </div>
              )}
              <span>INBOX</span>
            </div>
          </Link>

          {/* 登出选项 */}
          <button
            onClick={() => {
              handleLogoutClick();
              if (hideTimeout) {
                clearTimeout(hideTimeout);
              }
              onLeave();
            }}
            className='block w-full text-right px-2 py-3 text-sm font-semibold text-red-400 hover:bg-red-500 hover:text-white transition-colors duration-200 border-t border-gray-100'
          >
            Logout
          </button>
        </div>
      )}

      {/* 登出确认弹窗 */}
      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={handleCancelLogout}
        onConfirm={handleConfirmLogout}
        userEmail={user?.email || user?.user_metadata?.email}
      />
    </div>
  );
};

export default UserDropdown;
